import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate, useParams } from "react-router-dom";
import {
  InventoryProfileService,
  PaymentProfileService,
} from "subModule/src/core/services";
import {
  GetPurchasePaymentsInvoiceResponse,
  PurchasePayments,
} from "subModule/src/services/inventory/interface";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import BasicDatePicker from "common/Pickers/Pickers";
import { useGetVendorService } from "subModule/src/services/vendor/useVendor";
import DynamicSelect from "common/Select/Select";
import dayjs from "dayjs";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { PurchasePaymentInvoicesTable } from "components/Inventory/PurchasePaymentInvoicesTable";
import StyledInput from "common/SearchField/Search";
import { GetPaymentsCreditResponse } from "subModule/src/services/payment/interface";
import { useGetPaymentModesDropdownService } from "subModule/src/services/payment/usePayments";
import { useGetBankAccountsDropdownService } from "subModule/src/services/bankaccount/useBankAccount";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData {
  [key: string]: any;
}

const PurchasePaymentCreateUpdate: React.FC = () => {
  const params = useParams();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "vendors",
    moduleLabelKey: "supplierPayments",
    moduleHref: "/inventory/supplier-payments",
    entityFallbackLabelKey: "supplierPayment",
    isEdit: !!params?.id,
    action: "details",
  });
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const { enqueueSnackbar } = useSnackbar();
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    reset,
    resetField,
  } = useForm<FormData>({
    defaultValues: {
      purchaseInvoices: [],
      paymentDate: dayjs(new Date())?.format("YYYY-MM-DD"),
      receiptDate: dayjs(new Date())?.format("YYYY-MM-DD"),
      adjustmentType: { value: 591, label: t("FIFO") },
    },
  });
  const { data: bankAccountsData, refetch: bankAccountsRefetch } =
    useGetBankAccountsDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      search: dropdownSearchTerm,
    });
  const { data: vendorsData, refetch: vendorsRefetch } = useGetVendorService({
    pageNumber: dropdownPage,
    size: 50,
    title: dropdownSearchTerm,
  });
  const { data: paymentModesData, refetch: paymentModesRefetch } =
    useGetPaymentModesDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      title: dropdownSearchTerm,
    });
  const [purchaseInvoicesData, setPurchaseInvoicesData] = useState<any>({});
  const [payment, setPayment] = useState<PurchasePayments>();
  const [searchInvoice, setSearchInvoice] = useState("");
  const [searchTermInvoice, setSearchTermInvoice] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [loadingInvoices, setLoadingInvoices] = useState<boolean>(false);
  const [resetInvoices, setResetInvoices] = useState<boolean>(false);
  const [credit, setCredit] = useState<number>(0);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(5);
  const [invoicePageUpdate, setInvoicePageUpdate] = useState<boolean>(false);
  const paymentFetchedForId = useRef<string | null>(null);
  useEffect(() => {
    paymentModesRefetch();
    vendorsRefetch();
    bankAccountsRefetch();
    const id = params?.id;
    if (!id || paymentFetchedForId.current === id) return;
    paymentFetchedForId.current = id;
    if (loading) getPaymentById();
  }, [params?.id]);
  useEffect(() => {
    const getData = setTimeout(async () => {
      if (searchTermInvoice !== null && searching) {
        fetchInvoices(
          watch("supplierId").value,
          1,
          pageSize,
          searchTermInvoice,
        );
      }
    }, 300);
    return () => clearTimeout(getData);
  }, [searchTermInvoice, searching]);
  const getPaymentById = () => {
    InventoryProfileService.getEntityById(params?.id, "purchasepayment")
      .then((res: any) => {
        if (res.success) {
          setLoading(false);
          fetchCredit(res?.data?.supplierId || 0);
          if (res?.data?.adjustmentType === 592)
            fetchInvoices(res?.data?.supplierId || 0, 1, 5);
          reset({
            ...(res?.data || {}),
            supplierId: {
              value: res.data?.supplierId,
              label: res.data?.supplierName,
            },
            paymentMode: res.data?.paymentMode
              ? {
                  value: res.data?.paymentMode,
                  label: res?.data?.paymentModeTitle,
                }
              : null,
            adjustmentType: {
              value: res.data?.adjustmentType,
              label:
                adjustmentTypeOptions?.find(
                  (option: any) => option.value === res?.data?.adjustmentType,
                )?.label || res.data?.adjustmentType,
            },
          });
          setPayment({ ...(res?.data || {}) });
          captureLabel(
            res?.data?.paymentNumber != null
              ? String(res.data.paymentNumber)
              : null,
          );
        } else {
          setLoading(false);
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
      });
  };
  const fetchCredit = (supplierId: number) => {
    PaymentProfileService.getPaymentsCredit(
      supplierId,
      "purchasepayment/credit",
    )
      .then((response: GetPaymentsCreditResponse) => {
        if (response.success) {
          setCredit(response?.data || 0);
        } else {
          setLoading(false);
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
      });
  };
  const supplierOptions = (vendorsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const paymentModes = (paymentModesData?.pages[0]?.data?.content || []).map(
    (item?: any) => ({ value: item?.id, label: item?.name }),
  );
  const bankAccounts = (bankAccountsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; accountTitle: string }) => ({
      value: item.id,
      label: item.accountTitle,
    }),
  );
  const adjustmentTypeOptions = [
    { value: 591, label: t("FIFO") },
    { value: 592, label: t("MANUAL") },
  ];
  const columns = [
    {
      id: "purchaseInvoicesId",
      label: "",
      numeric: false,
      disablePadding: false,
    },
    { id: "invoiceNumber", label: t("supplierBillNo"), numeric: false },
    { id: "balanceAmount", label: t("balanceAmount"), numeric: false },
    { id: "totalAmount", label: t("totalAmount"), numeric: false },
  ];
  const fetchInvoices = (
    supplierId: number,
    page: number,
    size: number,
    invoiceNo?: any,
  ) => {
    InventoryProfileService.getPurchasePaymentsInvoice({
      page: page,
      size: size,
      supplierId: supplierId,
      invoiceNumber: invoiceNo,
    })
      .then((response: GetPurchasePaymentsInvoiceResponse) => {
        if (response.success) {
          setPurchaseInvoicesData(response?.data || {});
        } else {
          enqueueSnackbar(<AlertMessages text={response.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.data?.message || err || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => {
        setLoadingInvoices(false);
        setSearching(false);
        setInvoicePageUpdate(false);
      });
  };
  const clearSearchInvoice = () => {
    setSearching(true);
    setSearchInvoice("");
    setSearchTermInvoice("");
    if (searchTermInvoice) {
      handleSearch("");
    }
  };
  const handleSearch = (search: string) => {
    setSearching(true);
    setSearchTermInvoice(search);
  };
  const handleSearchClick = () => {
    const trimmedSearch = searchInvoice.trim();
    if (trimmedSearch !== "") {
      setSearching(true);
      handleSearch(trimmedSearch);
    }
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInvoice(event.target.value.trim());
    if (!event.target.value.trim()) handleSearch("");
  };
  const handleChangePage = (page: number) => {
    setInvoicePageUpdate(true);
    setPageSize(pageSize);
    fetchInvoices(
      watch("supplierId").value,
      page + 1,
      pageSize,
      searchTermInvoice,
    );
  };
  const handleChangeRows = (size: number) => {
    setInvoicePageUpdate(true);
    setPageSize(size);
    fetchInvoices(watch("supplierId").value, 1, size, searchTermInvoice);
  };
  const onSubmit = (data: FormData) => {
    if (Object.values(data)?.length) {
      setIsBtnEnabled(true);
      Object.keys(data).forEach((key) => {
        if (/^purchaseInvoices\d+$/.test(key)) {
          delete data[key];
        }
      });
      data.purchaseInvoices = (data.purchaseInvoices || []).map(
        (i: any) => i.id,
      );
      InventoryProfileService.createPurchasePayment({
        ...data,
        supplierId: data.supplierId.value,
        paymentMode: data?.paymentMode?.value || null,
        adjustmentType: data.adjustmentType.value,
        bankAccountId: data?.bankAccountId?.value || null,
      })
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("supplierPayment") +
                  " " +
                  t("created") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            // useCredit applies existing credit — no new payment id is created
            if (data.useCredit || !response?.data?.id) {
              navigate("/inventory/supplier-payments");
            } else {
              navigate(`/inventory/supplier-payment/${response.data.id}/print`);
            }
          } else {
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        })
        .finally(() => setIsBtnEnabled(false));
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "accounts")
        bankAccountsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "suppliers")
        vendorsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "paymentModes")
        paymentModesRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/inventory/supplier-payments")}
        onSubmit={() => {}}
        heading={t("supplierPayments")}
        icon={
          <InventoryIcon sx={{ fill: "#000", width: "28px", height: "28px" }} />
        }
        btnText={"back"}
        btnType={"back"}
        showCreateBtn={false}
      />
      {loading ? (
        <Loader />
      ) : (
        <Grid
          container
          component="form"
          id="form"
          className="px-[8px] items-baseline"
          rowGap={2.5}
          columnGap={2}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Grid container className="items-center">
            <Grid item xs={12} sm={6} className="text-start">
              <Typography
                className="font-Saira text-[15px] font-semibold"
                style={{ color: themeColors?.primary }}
              >
                {params?.id ? t("supplierPayment") : t("newSupplierPayment")}{" "}
                {t("Details")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} className="hide-on-print">
              <div className="flex gap-2 justify-end">
                {params?.id ? null : (
                  <Button
                    type="submit"
                    disabled={IsBtnEnabled}
                    className="primary"
                    style={{ background: themeColors?.primary }}
                    variant="contained"
                    disableElevation
                    onClick={() => onSubmit(handleSubmit(onSubmit))}
                  >
                    {t("save")}
                  </Button>
                )}
                {params?.id ? (
                  <>
                    {/* <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('email')}</Button> */}
                    <Button
                      className="secondary"
                      style={{ color: themeColors?.primary }}
                      variant="contained"
                      disableElevation
                      onClick={() =>
                        navigate(`${window.location.pathname}/print`)
                      }
                    >
                      {t("Print")}
                    </Button>
                  </>
                ) : null}
                <Button
                  className="secondary"
                  style={{ color: themeColors?.primary }}
                  variant="contained"
                  disableElevation
                  onClick={() => navigate("/inventory/supplier-payments")}
                >
                  {t("Back to list")}
                </Button>
              </div>
            </Grid>
          </Grid>
          {params?.id ? (
            <Grid
              container
              className="py-[12px] px-[18px] rounded-[8px] mb-[16px] items-center"
              style={{ background: themeColors?.primary }}
            >
              <Grid item xs={4}>
                <Typography className="font-Saira text-[15px] font-bold text-[#fff] text-left">
                  {t("supplierPayment")}
                </Typography>
                <Typography className="font-Saira text-[13px] text-[#fff] text-left">
                  <span className="font-bold">{t("Payment")} # </span>{" "}
                  <span>{payment?.paymentNumber || ""}</span>
                </Typography>
                <Typography className="font-Saira text-[13px] text-[#fff] text-left">
                  <span className="font-bold">
                    {t("payment") + " " + t("Date")}
                  </span>{" "}
                  <span>{payment?.paymentDate?.split("T")[0] || ""}</span>
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <div className="flex justify-end gap-10">
                  <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                    <span className="font-bold">
                      {t("Supplier")} {t("name")}
                    </span>
                    <br />
                    <span className="font-medium">
                      {payment?.supplierName || ""}
                    </span>
                  </Typography>
                  <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                    <span className="font-bold">{t("referenceNo")}</span> <br />
                    <span className="font-medium">
                      {payment?.referenceNumber || ""}
                    </span>
                  </Typography>
                  <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                    <span className="font-bold">{t("PaymentMode")}</span> <br />
                    <span className="font-medium">
                      {payment?.paymentModeTitle || ""}
                    </span>
                  </Typography>
                  <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                    <span className="font-bold">{t("adjustmentType")}</span>{" "}
                    <br />
                    <span className="font-medium">
                      {[
                        { value: 591, label: t("FIFO") },
                        { value: 592, label: t("MANUAL") },
                      ].find(
                        (option: any) =>
                          option.value === payment?.adjustmentType,
                      )?.label || ""}
                    </span>
                  </Typography>
                  <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                    <span className="font-bold">{t("recipentDate")}</span>{" "}
                    <br />
                    <span className="font-medium">
                      {payment?.receiptDate?.split("T")[0] || 0}
                    </span>
                  </Typography>
                  <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                    <span className="font-bold">{t("balanceAmount")}</span>{" "}
                    <br />
                    <span className="font-medium">
                      {new Intl.NumberFormat("en-US", {}).format(
                        payment?.balanceAmount || 0,
                      ) || 0}
                    </span>
                  </Typography>
                  <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                    <span className="font-bold">{t("amount")}</span> <br />
                    <span className="font-medium">
                      {new Intl.NumberFormat("en-US", {}).format(
                        payment?.amount || 0,
                      ) || 0}
                    </span>
                  </Typography>
                </div>
              </Grid>
            </Grid>
          ) : null}
          <Grid item xs={12} md={3}>
            <Controller
              name="supplierId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <DynamicSelect
                  label={t("vendors")}
                  defaultPlaceholder="phSelectSupplier"
                  {...field}
                  options={supplierOptions}
                  isSearchable
                  isRequired
                  onChange={(selectedOption: any) => {
                    field.onChange(selectedOption);
                    setResetInvoices(true);
                    setTimeout(() => fetchCredit(selectedOption.value), 700);
                    if (watch("adjustmentType").value === 592) {
                      setLoadingInvoices(true);
                      fetchInvoices(selectedOption.value, 1, pageSize);
                    }
                  }}
                  error={errors.supplierId?.message as string}
                  isDisabled={params?.id ? true : false}
                  isLoading={searchLoading === "suppliers"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      handleDropdownActions("suppliers", "searchIt", search);
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (dropdownSearchTerm || dropdownPage > 1)
                        handleDropdownActions("suppliers", "searchIt", "");
                      else setDropdownSearchTerm("");
                      setDropdownPage(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(vendorsData?.pages[0]?.data as any).last)
                      handleDropdownActions("suppliers", "loadNext");
                  }}
                  page={dropdownPage}
                />
              )}
            />
          </Grid>
          {watch("useCredit") ? null : (
            <Grid item xs={12} md={3}>
              <Controller
                name="bankAccountId"
                control={control}
                defaultValue={null}
                rules={{ required: t("This field is required.") }}
                render={({ field }) => (
                  <DynamicSelect
                    label={t("ACCOUNT") + " *"}
                    defaultPlaceholder="phSelectAccount"
                    {...field}
                    options={bankAccounts}
                    isSearchable
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                    }}
                    error={errors.bankAccountId?.message as string}
                    isLoading={searchLoading === "accounts"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        handleDropdownActions("accounts", "searchIt", search);
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (dropdownSearchTerm || dropdownPage > 1)
                          handleDropdownActions("accounts", "searchIt", "");
                        else setDropdownSearchTerm("");
                        setDropdownPage(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(bankAccountsData?.pages[0]?.data as any).last)
                        handleDropdownActions("accounts", "loadNext");
                    }}
                    page={dropdownPage}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography
              className="font-Saira text-[13px] leading-[16px] font-semibold text-left"
              style={{ color: themeColors?.primary }}
            >
              {t("supplierPayment")} {t("Details")}
            </Typography>
          </Grid>
          <Grid container item xs={12} spacing={2}>
            {watch("useCredit") ? null : (
              <Grid item xs={12} md={3}>
                <Controller
                  name="paymentMode"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("PaymentMode")}
                      defaultPlaceholder="phSelectPaymentMode"
                      {...field}
                      options={paymentModes}
                      isSearchable
                      isRequired
                      onChange={(selectedOption: any) =>
                        field.onChange(selectedOption)
                      }
                      error={errors.paymentMode?.message as string}
                      isDisabled={params?.id ? true : false}
                      isLoading={searchLoading === "paymentModes"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions(
                            "paymentModes",
                            "searchIt",
                            search,
                          );
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || dropdownPage > 1)
                            handleDropdownActions(
                              "paymentModes",
                              "searchIt",
                              "",
                            );
                          else setDropdownSearchTerm("");
                          setDropdownPage(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(paymentModesData?.pages[0]?.data as any).last)
                          handleDropdownActions("paymentModes", "loadNext");
                      }}
                      page={dropdownPage}
                    />
                  )}
                />
              </Grid>
            )}
            {watch("useCredit") ? null : (
              <Grid item xs={12} md={3}>
                <TextField
                  type="number"
                  inputProps={{ step: "any", min: 0 }}
                  {...register("amount", {
                    required: t("This field is required."),
                    validate: (value) =>
                      value > 0 || t("amountGreaterThanZero"),
                  })}
                  placeholder={t("phSupplierPaymentAmount")}
                  label={t("amount") + " *"}
                  title={t("amount")}
                  // onChange={(e: any) => {
                  //     const value = e.target.value;
                  //     e.target.value = value < 0 ? 0 : value || "";
                  // }}
                  fullWidth
                  error={!!errors.amount}
                  helperText={errors.amount?.message as string}
                  InputLabelProps={{ shrink: true }}
                  disabled={params?.id as any}
                />
              </Grid>
            )}
            <Grid item xs={12} md={3}>
              <TextField
                {...register("referenceNumber", {
                  required: t("This field is required."),
                })}
                placeholder={t("phSupplierPaymentReferenceNo")}
                label={t("referenceNo") + " *"}
                title={t("referenceNo")}
                fullWidth
                error={!!errors.referenceNumber}
                helperText={errors.referenceNumber?.message as string}
                InputLabelProps={{ shrink: true }}
                disabled={params?.id as any}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="adjustmentType"
                control={control}
                defaultValue={null}
                rules={{
                  required: t("This field is required."),
                  validate: (value) => {
                    if (
                      value?.value === 592 &&
                      watch("purchaseInvoices")?.length === 0
                    ) {
                      return t("atLeastOneSupplierBill");
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <DynamicSelect
                    label={t("adjustmentType")}
                    {...field}
                    options={adjustmentTypeOptions}
                    isSearchable
                    isRequired
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setResetInvoices(true);
                      if (watch("supplierId") && selectedOption.value === 592) {
                        setLoadingInvoices(true);
                        fetchInvoices(watch("supplierId").value, 1, pageSize);
                      }
                    }}
                    error={errors.adjustmentType?.message as string}
                    isDisabled={params?.id ? true : false}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="paymentDate"
                control={control}
                rules={{
                  validate: (value) => {
                    const date = new Date(value);
                    return isNaN(date.getTime()) ? t("Invalid Date") : true;
                  },
                }}
                render={({ field }) => (
                  <BasicDatePicker
                    type="datePicker"
                    {...field}
                    label={t("Payment") + " " + t("Date")}
                    fullWidth
                    onChange={(value: any) =>
                      field.onChange(
                        new Date(value)
                          .toLocaleDateString("en-CA", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                          ?.split("/")
                          .reverse()
                          .join("-"),
                      )
                    }
                    required={true}
                    error={errors.paymentDate?.message as string}
                    disable={params?.id}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="receiptDate"
                control={control}
                rules={{
                  validate: (value) => {
                    const date = new Date(value);
                    return isNaN(date.getTime()) ? t("Invalid Date") : true;
                  },
                }}
                render={({ field }) => (
                  <BasicDatePicker
                    type="datePicker"
                    {...field}
                    label={t("recipentDate")}
                    fullWidth
                    onChange={(value: any) =>
                      field.onChange(
                        new Date(value)
                          .toLocaleDateString("en-CA", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                          ?.split("/")
                          .reverse()
                          .join("-"),
                      )
                    }
                    required={true}
                    error={errors.receiptDate?.message as string}
                    disable={params?.id}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="useCredit"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <Box sx={{ "& label": { width: "fit-content" } }}>
                    <CommonCheckbox
                      {...field}
                      label={t("useCredit")}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        field.onChange(event.target.checked);
                        setValue("paymentMode", null);
                        setValue("amount", 0);
                        setValue("bankAccountId", null);
                      }}
                      checked={field.value}
                      color={themeColors?.primary}
                      disabled={params?.id ? true : false}
                    />
                  </Box>
                )}
              />
              {watch("supplierId") ? (
                <Typography className="font-Saira text-start font-[600] text-[17px] leading-[22px]">
                  {t("creditAmount")} = {credit || 0}
                </Typography>
              ) : null}
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                {...register("remarks")}
                label={t("remarks")}
                title={t("remarks")}
                placeholder={t("phSupplierPaymentRemarks")}
                fullWidth
                error={!!errors.remarks}
                helperText={errors.remarks?.message as string}
                InputLabelProps={{ shrink: true }}
                multiline
                rows={4}
                sx={{
                  "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                    width: "100%",
                  },
                }}
                disabled={params?.id ? true : false}
              />
            </Grid>
          </Grid>
          {watch("supplierId") && watch("adjustmentType").value === 592 ? (
            <Grid container>
              {loadingInvoices ? (
                <CircularProgress
                  className="block m-auto"
                  sx={{ color: themeColors?.primary, width: "100%" }}
                  size={24}
                />
              ) : (
                <Grid
                  container
                  spacing={1}
                  className="bg-white ps-[12px] pe-[18px] py-[6px] rounded-[8px] relative"
                >
                  {searching || invoicePageUpdate ? <Loader /> : null}
                  <Grid item xs={12}>
                    <Typography className="font-Saira text-[11px] font-semibold text-[#171826] text-left">
                      {t("unpaidSupplierBills")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={7} lg={5}>
                    <StyledInput
                      fullWidth
                      placeholder={t("phSupplierBillNo")}
                      handleSearchClick={handleSearchClick}
                      handleChange={handleInputChange}
                      value={searchInvoice}
                      clearSearch={clearSearchInvoice}
                    />
                  </Grid>
                  {watch(`purchaseInvoices`)?.length > 0 ? (
                    <Grid item xs={12} md={5} lg={7}>
                      <Typography
                        className="font-semibold font-Saira text-[14px] flex gap-1 justify-end"
                        sx={{ color: themeColors?.primary }}
                      >
                        <span className="font-medium">
                          {t("total") + " " + t("dueAmount")}
                        </span>
                        <span>:</span>
                        <span className="font-bold">
                          {new Intl.NumberFormat("en", {
                            notation: "standard",
                            maximumFractionDigits: 2,
                          }).format(
                            (watch("purchaseInvoices") || []).reduce(
                              (sum: number, inv: any) =>
                                sum + (inv.balanceAmount || 0),
                              0,
                            ),
                          )}
                        </span>
                      </Typography>
                    </Grid>
                  ) : null}
                  <Grid item xs={12}>
                    <PurchasePaymentInvoicesTable
                      rows={purchaseInvoicesData?.content || []}
                      columns={columns}
                      handleChangePage={handleChangePage}
                      handleChangeRows={handleChangeRows}
                      tableData={purchaseInvoicesData || {}}
                      {...{ pageSize }}
                      form={{
                        register,
                        control,
                        handleSubmit,
                        formState: { errors },
                        reset,
                        resetField,
                        watch,
                        setValue,
                        resetInvoices,
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          ) : null}
          <Grid item xs={12} className="hide-on-print">
            <Divider className="bg-[#AEB3C4] mt-[8px]" />
          </Grid>
          <Grid container className="items-center hide-on-print">
            <Grid item xs={12} sm={6} className="text-start"></Grid>
            <Grid item xs={12} sm={6}>
              <div className="flex gap-2 justify-end">
                {params?.id ? null : (
                  <Button
                    type="submit"
                    disabled={IsBtnEnabled}
                    className="primary"
                    style={{ background: themeColors?.primary }}
                    variant="contained"
                    disableElevation
                    onClick={() => onSubmit(handleSubmit(onSubmit))}
                  >
                    {t("save")}
                  </Button>
                )}
                {params?.id ? (
                  <>
                    {/*  <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('email')}</Button> */}
                    <Button
                      className="secondary"
                      variant="contained"
                      style={{ color: themeColors?.primary }}
                      disableElevation
                      onClick={() =>
                        navigate(`${window.location.pathname}/print`)
                      }
                    >
                      {t("Print")}
                    </Button>
                  </>
                ) : null}
                <Button
                  className="secondary"
                  variant="contained"
                  style={{ color: themeColors?.primary }}
                  disableElevation
                  onClick={() => navigate("/inventory/supplier-payments")}
                >
                  {t("Back to list")}
                </Button>
              </div>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default PurchasePaymentCreateUpdate;
